import React, { useEffect, useState } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Modal,
  FlatList,
} from "react-native";
import { getFunctions, httpsCallable } from "firebase/functions";
import Toast from "react-native-toast-message";
import { Ionicons } from "@expo/vector-icons";

import { doc, setDoc, getDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";

const AltaViajante = ({ navigation }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(false);
  const [vendedores, setVendedores] = useState([]);
  const [selectedVendedores, setSelectedVendedores] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loadingVendedores, setLoadingVendedores] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Fetch vendedores from vendedoresdrn collection
  useEffect(() => {
    fetchVendedores();
  }, []);

  const fetchVendedores = async () => {
    setLoadingVendedores(true);
    try {
      const vendedoresRef = collection(db, "vendedoresdrn");
      const snapshot = await getDocs(vendedoresRef);
      const vendedoresList = [];
      snapshot.forEach((doc) => {
        vendedoresList.push({
          id: doc.id,
          ...doc.data(),
        });
      });
      setVendedores(vendedoresList);
    } catch (error) {
      console.error("Error fetching vendedores:", error);
      Alert.alert("Error", "No se pudieron cargar los vendedores de 3C");
    } finally {
      setLoadingVendedores(false);
    }
  };

  const handleToggleVendedor = (vendedor) => {
    setSelectedVendedores((prev) => {
      const isSelected = prev.some((v) => v.id === vendedor.id);
      if (isSelected) {
        return prev.filter((v) => v.id !== vendedor.id);
      } else {
        return [...prev, vendedor];
      }
    });
  };

  const handleCreateUser = async () => {
    if (!email || !password || !displayName) {
      Alert.alert("Error", "Por favor complete todos los campos");
      return;
    }

    if (selectedVendedores.length === 0) {
      Alert.alert("Error", "Por favor seleccione al menos una cuenta de 3C");
      return;
    }

    setLoading(true);
    try {
      const functions = getFunctions();
      const createUserFn = httpsCallable(functions, "createNewUser");
      const result = await createUserFn({
        email,
        password,
        displayName,
        photoURL: "VIAJANTE",
      });
      
      // Save viajante with vendedores data (multiple accounts)
      await setDoc(
        doc(db, "viajantes", email),
        {
          name: displayName,
          email: email,
          // Save array of vendedor documents
          vendedores3C: selectedVendedores.map((v) => ({ ...v })),
          connectedAt: new Date().toISOString(),
        },
        { merge: true }
      );
      
      Toast.show({
        type: "success",
        text1: `Viajante ${displayName} creado con éxito`,
      });

      // Reset form
      setEmail("");
      setPassword("");
      setDisplayName("");
      setSelectedVendedores([]);
    } catch (error) {
      console.error(error);
      Alert.alert("❌ Error", error.message || "Could not create user.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === "ios" ? "padding" : undefined}
      style={localStyles.container}
    >
      <View style={localStyles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={localStyles.backButton}>
          <Ionicons name="arrow-back" size={24} color="#fff" />
        </TouchableOpacity>
        <Text style={localStyles.headerTitle}>Nuevo Viajante</Text>
      </View>

      <ScrollView
        contentContainerStyle={localStyles.scrollContainer}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <View style={localStyles.formCard}>
          <Text style={localStyles.formTitle}>Información del Viajante</Text>

          {/* Name Input */}
          <View style={localStyles.inputContainer}>
            <Text style={localStyles.inputLabel}>Nombre Completo</Text>
            <View style={localStyles.inputWrapper}>
              <Ionicons name="person-outline" size={20} color="#666" style={localStyles.inputIcon} />
              <TextInput
                style={localStyles.input}
                placeholder="Ej: Juan Pérez"
                value={displayName}
                onChangeText={setDisplayName}
                placeholderTextColor="#999"
              />
            </View>
          </View>

          {/* Email Input */}
          <View style={localStyles.inputContainer}>
            <Text style={localStyles.inputLabel}>Correo Electrónico</Text>
            <View style={localStyles.inputWrapper}>
              <Ionicons name="mail-outline" size={20} color="#666" style={localStyles.inputIcon} />
              <TextInput
                style={localStyles.input}
                placeholder="correo@ejemplo.com"
                autoCapitalize="none"
                keyboardType="email-address"
                value={email}
                onChangeText={setEmail}
                placeholderTextColor="#999"
              />
            </View>
          </View>

          {/* Password Input */}
          <View style={localStyles.inputContainer}>
            <Text style={localStyles.inputLabel}>Contraseña</Text>
            <View style={localStyles.inputWrapper}>
              <Ionicons name="lock-closed-outline" size={20} color="#666" style={localStyles.inputIcon} />
              <TextInput
                style={localStyles.input}
                placeholder="Contraseña segura"
                secureTextEntry={!showPassword}
                value={password}
                onChangeText={setPassword}
                placeholderTextColor="#999"
              />
              <TouchableOpacity
                onPress={() => setShowPassword(!showPassword)}
                style={localStyles.eyeIcon}
              >
                <Ionicons
                  name={showPassword ? "eye-outline" : "eye-off-outline"}
                  size={20}
                  color="#666"
                />
              </TouchableOpacity>
            </View>
          </View>

          {/* Conectar con cuentas de 3C */}
          <View style={localStyles.inputContainer}>
            <Text style={localStyles.sectionLabel}>Cuentas de 3C</Text>
            <TouchableOpacity
              style={localStyles.selectButton}
              onPress={() => setShowModal(true)}
              disabled={loadingVendedores}
            >
              {loadingVendedores ? (
                <ActivityIndicator size="small" color="#2a2a8e" />
              ) : (
                <View style={localStyles.selectButtonContent}>
                  <Ionicons
                    name="briefcase-outline"
                    size={20}
                    color={selectedVendedores.length > 0 ? "#2a2a8e" : "#666"}
                    style={localStyles.inputIcon}
                  />
                  <Text style={[
                      localStyles.selectButtonText,
                      selectedVendedores.length > 0 && localStyles.selectButtonTextSelected
                    ]}>
                    {selectedVendedores.length > 0
                      ? `${selectedVendedores.length} cuenta(s) seleccionada(s)`
                      : "Conectar con cuentas de 3C"}
                  </Text>
                  <Ionicons name="chevron-down" size={20} color="#666" style={{marginLeft: "auto"}} />
                </View>
              )}
            </TouchableOpacity>

            {selectedVendedores.length > 0 && (
              <View style={localStyles.selectedInfoContainer}>
                {selectedVendedores.map((vendedor) => (
                  <View key={vendedor.id} style={localStyles.selectedInfo}>
                    <Ionicons name="checkmark-circle" size={16} color="#2E7D32" />
                    <Text style={localStyles.selectedText}>
                      {vendedor.APELLIDOYNOMBRE}
                    </Text>
                    <TouchableOpacity
                      onPress={() => handleToggleVendedor(vendedor)}
                      style={localStyles.removeButton}
                    >
                      <Ionicons name="close-circle" size={18} color="#666" />
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
            )}
          </View>

          <TouchableOpacity
            style={[localStyles.button, loading && localStyles.buttonDisabled]}
            onPress={handleCreateUser}
            disabled={loading}
          >
            {loading ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <>
                <Text style={localStyles.buttonText}>Crear Usuario</Text>
                <Ionicons name="arrow-forward" size={20} color="#fff" />
              </>
            )}
          </TouchableOpacity>
        </View>
      </ScrollView>

      {/* Modal to select vendedores (multi-select) */}
      <Modal
        visible={showModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowModal(false)}
      >
        <View style={localStyles.modalOverlay}>
          <View style={localStyles.modalContainer}>
            <View style={localStyles.modalHeader}>
              <Text style={localStyles.modalTitle}>Seleccionar Vendedores</Text>
              <TouchableOpacity
                onPress={() => setShowModal(false)}
                style={localStyles.closeButton}
              >
                <Ionicons name="close" size={24} color="#666" />
              </TouchableOpacity>
            </View>

            {selectedVendedores.length > 0 && (
              <View style={localStyles.modalSelectedCount}>
                <Text style={localStyles.modalSelectedCountText}>
                  {selectedVendedores.length} seleccionado(s)
                </Text>
              </View>
            )}

            <FlatList
              data={vendedores}
              keyExtractor={(item) => item.id}
              contentContainerStyle={localStyles.listContent}
              renderItem={({ item }) => {
                const isSelected = selectedVendedores.some((v) => v.id === item.id);
                return (
                  <TouchableOpacity
                    style={[
                      localStyles.vendedorItem,
                      isSelected && localStyles.vendedorItemSelected,
                    ]}
                    onPress={() => handleToggleVendedor(item)}
                  >
                    <View style={[
                      localStyles.vendedorAvatar,
                      isSelected && localStyles.vendedorAvatarSelected
                    ]}>
                      <Text style={localStyles.vendedorAvatarText}>
                        {item.APELLIDOYNOMBRE ? item.APELLIDOYNOMBRE.charAt(0) : "?"}
                      </Text>
                    </View>
                    <View style={localStyles.vendedorInfo}>
                      <Text style={localStyles.vendedorName}>
                        {item.APELLIDOYNOMBRE || "Sin nombre"}
                      </Text>
                      {item.CODIGO && (
                        <Text style={localStyles.vendedorCode}>Código: {item.CODIGO}</Text>
                      )}
                    </View>
                    <View style={[
                      localStyles.checkbox,
                      isSelected && localStyles.checkboxSelected
                    ]}>
                      {isSelected && (
                        <Ionicons name="checkmark" size={16} color="#fff" />
                      )}
                    </View>
                  </TouchableOpacity>
                );
              }}
              ListEmptyComponent={
                <View style={localStyles.emptyContainer}>
                  <Ionicons name="search" size={48} color="#ddd" />
                  <Text style={localStyles.emptyText}>
                    No se encontraron vendedores
                  </Text>
                </View>
              }
            />

            <View style={localStyles.modalFooter}>
              <TouchableOpacity
                style={localStyles.confirmButton}
                onPress={() => setShowModal(false)}
              >
                <Text style={localStyles.confirmButtonText}>Confirmar</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </KeyboardAvoidingView>
  );
};

export default AltaViajante;

const localStyles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F4F6FD",
  },
  header: {
    backgroundColor: "#2a2a8e",
    paddingTop: 50,
    paddingBottom: 20,
    paddingHorizontal: 20,
    flexDirection: "row",
    alignItems: "center",
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  backButton: {
    padding: 4,
    marginRight: 12,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#fff",
  },
  scrollContainer: {
    padding: 20,
  },
  formCard: {
    backgroundColor: "#fff",
    borderRadius: 16,
    padding: 24,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  formTitle: {
    fontSize: 20,
    fontWeight: "600",
    color: "#2a2a8e",
    marginBottom: 24,
  },
  inputContainer: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#333",
    marginBottom: 8,
  },
  inputWrapper: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F4F6FD",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E0E0E0",
    paddingHorizontal: 12,
  },
  inputIcon: {
    marginRight: 10,
  },
  input: {
    flex: 1,
    paddingVertical: 14,
    fontSize: 16,
    color: "#333",
  },
  eyeIcon: {
    padding: 4,
  },
  sectionLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#333",
    marginBottom: 8,
  },
  selectButton: {
    backgroundColor: "#F4F6FD",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E0E0E0",
    padding: 14,
    minHeight: 52,
    justifyContent: "center",
  },
  selectButtonContent: {
    flexDirection: "row",
    alignItems: "center",
  },
  selectButtonText: {
    color: "#999",
    fontSize: 16,
  },
  selectButtonTextSelected: {
    color: "#333",
    fontWeight: "500",
  },
  selectedInfoContainer: {
    marginTop: 8,
  },
  selectedInfo: {
    flexDirection: "row",
    alignItems: "center",
    padding: 10,
    backgroundColor: "#E8F5E9",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#C8E6C9",
    marginBottom: 6,
  },
  selectedText: {
    color: "#2E7D32",
    fontSize: 14,
    fontWeight: "500",
    marginLeft: 8,
    flex: 1,
  },
  removeButton: {
    padding: 2,
  },
  button: {
    backgroundColor: "#2a2a8e",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 16,
    borderRadius: 12,
    marginTop: 12,
    shadowColor: "#2a2a8e",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 4,
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  buttonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "600",
    marginRight: 8,
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "flex-end",
  },
  modalContainer: {
    backgroundColor: "#fff",
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    height: "80%",
    paddingBottom: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.1,
    shadowRadius: 10,
    elevation: 10,
  },
  modalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#333",
  },
  closeButton: {
    padding: 4,
  },
  listContent: {
    padding: 16,
  },
  vendedorItem: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    marginBottom: 8,
    backgroundColor: "#fff",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E0E0E0",
  },
  vendedorItemSelected: {
    backgroundColor: "#E3F2FD",
    borderColor: "#2a2a8e",
  },
  vendedorAvatarSelected: {
    backgroundColor: "#2a2a8e",
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: "#ccc",
    justifyContent: "center",
    alignItems: "center",
  },
  checkboxSelected: {
    backgroundColor: "#2a2a8e",
    borderColor: "#2a2a8e",
  },
  modalSelectedCount: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    backgroundColor: "#E3F2FD",
  },
  modalSelectedCountText: {
    color: "#2a2a8e",
    fontWeight: "600",
    fontSize: 14,
  },
  modalFooter: {
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: "#eee",
  },
  confirmButton: {
    backgroundColor: "#2a2a8e",
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: "center",
  },
  confirmButtonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "600",
  },
  vendedorAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#F4F6FD",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 12,
  },
  vendedorAvatarText: {
    color: "#2a2a8e",
    fontWeight: "600",
    fontSize: 16,
  },
  vendedorInfo: {
    flex: 1,
  },
  vendedorName: {
    fontSize: 15,
    fontWeight: "600",
    color: "#333",
    marginBottom: 2,
  },
  vendedorCode: {
    fontSize: 13,
    color: "#666",
  },
  emptyContainer: {
    alignItems: "center",
    justifyContent: "center",
    padding: 40,
  },
  emptyText: {
    textAlign: "center",
    marginTop: 16,
    color: "#999",
    fontSize: 16,
  },
});
